import { RestManager } from '@/newApi/services/rest/RestManager';
import { getApiHost } from '@/appSettings';
import type {
    MailTemplate,
    PerformanceAssessmentPublishKeyValues,
    BudgetPublishKeyValues,
    PerformanceAssessmentPublishParams,
    Review,
    MissingEmploymentsKeyValues,
    EmploymentTableRow,
    ResultPerformanceChartGroup,
    ResultSalaryKeyValues,
    LegacySalaryReviewSettings,
    Organisation,
    SyncData,
    SalaryReviewItem,
    CurrencyConversion,
    ReportBudgetResponse
} from '@sysarb/sysarb-types-v2/services/v1/pay-management';
import type { StorageFilterGroup } from '@sysarb/sysarb-types-v2/services/v1/filter-storage';
import { Role } from '@sysarb/sysarb-types/service-base/enums';
import type { useResgate } from '@/login/composables/useResgate';
import { useLoginStore } from '@/login/stores/useLoginStore';
import type { PerformanceAssessmentPublishStatus, SalaryReviewListTypes } from '@sysarb/sysarb-types-v2/services/v1/pay-management/enums';

const payManagementService = new RestManager(`https://${getApiHost()}/pay-management-service`);

export function abort(): void {
    payManagementService.abort();
}

/**
 * Update Review.
 * @param reviewId Pay management review id
 * @param review Pay management review to update
 */
export function updateReview(resgate: ReturnType<typeof useResgate>, reviewId: string, review: Review): void {
    const payload: Review = {
        id: review.id,
        subdomain: review.subdomain,
        createdAt: review.createdAt,
        updatedAt: review.updatedAt,
        filter: review.filter,
        salaryReviewID: review.salaryReviewID,
        reviewDraftID: review.reviewDraftID,
        budgetSettingStatus: review.budgetSettingStatus,
        budgetSettingScope: review.budgetSettingScope,
        budgetSettingStyle: review.budgetSettingStyle,
        budgetPublished: review.budgetPublished,
        budgetSettingSalaryField: review.budgetSettingSalaryField,
        budgetSettingsDone: review.budgetSettingsDone,
        performanceAssessmentPublishStatus: review.performanceAssessmentPublishStatus,
        salaryYear: review.salaryYear,
        isLegacy: review.isLegacy,
        allowUnionSuggestedSalary: review.allowUnionSuggestedSalary,
    };
    void resgate.call(`v1.pay-management.review.${reviewId}`, 'update', payload);
}

/**
 * Start Review creation process
 * @param reviewDraftId Pay management review draft id
 */
export async function createReview(resgate: ReturnType<typeof useResgate>, reviewDraftId: string): Promise<Review | null> {
    const promise = resgate.call<Review>(`v1.pay-management.review-draft.${reviewDraftId}`, 'start', {}).then(res => {
        if (res?.success === false) return null;
        return res.data;
    });
    return promise;
}

/**
 * Get Review by salary review id
 *  @param salaryReviewId Salary review id
 *  @param componentId Id of component calling the function
 */
export async function getReviewBySalaryReviewId(resgate: ReturnType<typeof useResgate>, salaryReviewId: string): Promise<Review | null> {
    const { instanceTopic } = useLoginStore();
    if (instanceTopic == null) {
        return Promise.resolve(null);
    }
    const promise = resgate.model<Review>(`v1.pay-management.subdomain.${instanceTopic}.review.${salaryReviewId}.getbysalaryreviewid`).then(res => {
        if (res?.success === false) return null;
        return res.data;
    });
    return promise;
}

/**
 * Get Review by draft id
 *  @param reviewDraftId Salary review id
 */
export async function getReviewByReviewDraftId(resgate: ReturnType<typeof useResgate>, reviewDraftId: string): Promise<Review | null> {
    const promise = resgate.model<Review>(`v1.pay-management.review.${reviewDraftId}.getbyreviewdraftid`).then(res => {
        if (res?.success === false) return null;
        return res.data;
    });
    return promise;
}

/**
 * Get Review by draft id
 *  @param reviewId Salary review id
 */
export async function getBudgetPublishKeyValues(resgate: ReturnType<typeof useResgate>, reviewId: string): Promise<BudgetPublishKeyValues | null> {
    const promise = resgate.call<BudgetPublishKeyValues>(`v1.pay-management.budget.${reviewId}`, 'publishkeyvalues', {}).then(res => {
        if (res?.success === false) return null;
        return res.data;
    });
    return promise;
}

/**
 * Get Review by draft id
 *  @param reviewId Salary review id
 */
export async function getAssessmentPublishKeyValues(resgate: ReturnType<typeof useResgate>, reviewId: string): Promise<PerformanceAssessmentPublishKeyValues | null> {
    const promise = resgate.call<PerformanceAssessmentPublishKeyValues>(`v1.pay-management.performance-assessment.${reviewId}`, 'publishkeyvalues', {}).then(res => {
        if (res?.success === false) return null;
        return res.data;
    });
    return promise;
}

/**
 * Get Review by draft id
 *  @param reviewDraftId Salary review id
 */
export async function publishBudgets(resgate: ReturnType<typeof useResgate>, reviewId: string, templates: MailTemplate): Promise<unknown> {
    const promise = resgate.call(`v1.pay-management.budget.${reviewId}`, 'publish', templates);
    return promise;
}

/**
 * Get Review by draft id
 *  @param reviewDraftId Salary review id
 */
export async function unPublishBudgets(resgate: ReturnType<typeof useResgate>, reviewId: string, templates: MailTemplate): Promise<unknown> {
    const promise = resgate.call(`v1.pay-management.budget.${reviewId}`, 'unpublish', templates);
    return promise;
}

/**
 * Get Review by draft id
 *  @param reviewDraftId Salary review id
 */
export async function publishPerformanceAssessment(
    resgate: ReturnType<typeof useResgate>,
    reviewId: string,
    publishStatus: PerformanceAssessmentPublishStatus,
    mailTemplate: MailTemplate
): Promise<unknown> {
    const params: PerformanceAssessmentPublishParams = {
        publishStatus,
        mailTemplate,
    };
    const promise = resgate.call(`v1.pay-management.performance-assessment.${reviewId}`, 'publish', params);
    return promise;
}

/**
 * Get Review by draft id
 *  @param reviewDraftId Salary review id
 */
export async function unPublishPerformanceAssessment(
    resgate: ReturnType<typeof useResgate>,
    reviewId: string,
    mailTemplate: MailTemplate
): Promise<unknown> {
    const promise = resgate.call(`v1.pay-management.performance-assessment.${reviewId}`, 'unpublish', mailTemplate);
    return promise;
}

/**
 * Get newly matched employments
 * @param reviewId review id
 */
export async function getMissingTableData(reviewId: string): Promise<EmploymentTableRow[]> {
    const { instance } = useLoginStore();
    return payManagementService.post<EmploymentTableRow[]>({}, `v1/${instance}/pay-management/employments/${reviewId}/getmissingtabledata`);
}

/**
 * Get excluded employments
 * @param reviewId review id
 */
export async function getexcludedtabledata(reviewId: string): Promise<EmploymentTableRow[]> {
    const { instance } = useLoginStore();
    return payManagementService.post<EmploymentTableRow[]>({}, `v1/${instance}/pay-management/employments/${reviewId}/getexcludedtabledata`);
}

/**
 * Get new filter table data
 * @param reviewId review id
 */
export async function getNewFilterTableData(reviewId: string, filter: StorageFilterGroup): Promise<EmploymentTableRow[]> {
    const { instance } = useLoginStore();
    return payManagementService.post<EmploymentTableRow[]>({ filter }, `v1/${instance}/pay-management/employments/${reviewId}/getnewfiltertabledata`);
}

/**
 * Add employments to the review
 *  @param reviewId Review id
 *  @param componentId Id of component calling the function
 *  @returns Syncjob id
 */
export async function addEmploymentsToReview(resgate: ReturnType<typeof useResgate>, reviewId: string, employmentIds: string[]): Promise<SyncData | null> {
    const promise = resgate.call<SyncData>(`v1.pay-management.review.${reviewId}`, 'addemployments', employmentIds).then(res => {
        if (res?.success === false) return null;
        return res.data;
    });
    return promise;
}

/**
 * Remove employments from the review
 *  @param reviewId Review id
 *  @param componentId Id of component calling the function
 *  @returns Syncjob id
 */
export async function removeEmploymentsFromReview(resgate: ReturnType<typeof useResgate>, reviewId: string, employmentIds: string[]): Promise<SyncData | null> {
    const promise = resgate.call<SyncData>(`v1.pay-management.review.${reviewId}`, 'removeemployments', employmentIds).then(res => {
        if (res?.success === false) return null;
        return res.data;
    });
    return promise;
}

/**
 * Ignore missing employments
 *  @param reviewId Review id
 *  @param componentId Id of component calling the function
 */
export async function ignoreMissingEmployments(resgate: ReturnType<typeof useResgate>, reviewId: string, employmentIds: string[]): Promise<unknown> {
    const promise = resgate.call(`v1.pay-management.employments.${reviewId}`, 'ignoremissingemployments', employmentIds);
    return promise;
}

/**
 * activate send salaries for managers.
 *  @param reviewId Salary review id
 *  @param sendSalaries Allow managers to send salary
 */
export async function updateactivatesendnewsalaries(resgate: ReturnType<typeof useResgate>, reviewId: string, sendSalaries: boolean): Promise<unknown> {
    const promise = resgate.call(`v1.pay-management.review.${reviewId}`, 'updateactivatesendnewsalaries', { value: sendSalaries });
    return promise;
}

/**
 * Get keyvalues for filter selection.
 *  @param reviewId Review id
 *  @param componentId Id of component calling the function
 */
export async function getFilterSelectionEmploymentKeyValues(resgate: ReturnType<typeof useResgate>, reviewId: string): Promise<MissingEmploymentsKeyValues | null> {
    const { instanceTopic } = useLoginStore();
    if (instanceTopic == null) {
        return Promise.resolve(null);
    }
    const promise = resgate.model<MissingEmploymentsKeyValues>(`v1.pay-management.subdomain.${instanceTopic}.review.${reviewId}.employmenttotals`).then(res => {
        if (res?.success === false) return null;
        return res.data;
    });
    return promise;
}

/**
 * Get legacy review settings
 *  @param salaryReviewId Salary review id
 *  @param componentId Id of component calling the function
 */
export async function getReviewSettings(resgate: ReturnType<typeof useResgate>, salaryReviewId: string): Promise<LegacySalaryReviewSettings | null> {
    const { instanceTopic } = useLoginStore();
    if (instanceTopic == null) {
        return Promise.resolve(null);
    }
    const promise = resgate.model<LegacySalaryReviewSettings>(`v1.pay-management.subdomain.${instanceTopic}.review.${salaryReviewId}.salaryreviewsettings`).then(res => {
        if (res?.success === false) return null;
        return res.data;
    });
    return promise;
}

/**
 * Fetch result key values
 * @param reviewId Salary review id
 */
export async function getResultKeyValues(reviewId: string, filter: StorageFilterGroup): Promise<ResultSalaryKeyValues> {
    const { instance } = useLoginStore();
    return payManagementService.post<ResultSalaryKeyValues>({ filter }, `v1/${instance}/pay-management/review-employments/${reviewId}/resultkeyvalues`);
}

/**
 * Fetch result performance chart data
 * @param reviewId Salary review id
 */
export async function getResultPerformanceChartKeyValues(reviewId: string, filter: StorageFilterGroup): Promise<ResultPerformanceChartGroup[]> {
    const { instance } = useLoginStore();
    return payManagementService.post<ResultPerformanceChartGroup[]>({ filter }, `v1/${instance}/pay-management/review-employments/${reviewId}/resultperformancechart`);
}

/**
 * Fetch all organisationNodes connected to a salary review
 * @param salaryReviewId Salary review id
 */
export async function getAllOrganisationNodes(salaryReviewId: string): Promise<Organisation> {
    const { instance } = useLoginStore();
    return payManagementService.post<Organisation>({}, `v1/${instance}/pay-management/organisation-nodes/${salaryReviewId}/all`);
}

/**
 * Get latest syncjob
 *  @param reviewId Review id
 */
export async function getLatestSyncJob(resgate: ReturnType<typeof useResgate>, reviewId: string): Promise<SyncData | null> {
    const promise = resgate.call<SyncData>(`v1.pay-management.review.${reviewId}`, 'latestsyncjob', {}).then(res => {
        if (res?.success === false) return null;
        return res.data;
    });
    return promise;
}

/**
 * Set the syncjobs status to timed out
 *  @param syncJobId syncJob id
 */
export async function setSyncJobTimout(resgate: ReturnType<typeof useResgate>, syncJobId: string): Promise<void | null> {
    const promise = resgate.call<void>(`v1.pay-management.syncing.${syncJobId}`, 'syncjobtimeout', {}).then(res => {
        if (res?.success === false) return null;
        return res.data;
    });
    return promise;
}

/**
 * Get menu list
 *  @param currentRole current user role
 *  @param currentOrganisationId current organisation id
 *  @param type Salary review list type
 *  @param componentId Id of component calling the function
 */
export async function activeSalaryReviewList(resgate: ReturnType<typeof useResgate>, type: SalaryReviewListTypes): Promise<Iterable<SalaryReviewItem> | null> {
    const { instanceTopic, role, currentOrganisationNodeId } = useLoginStore();
    if (instanceTopic == null) {
        return Promise.resolve(null);
    }

    if (role == null || currentOrganisationNodeId == null) {
        return Promise.resolve(null);
    }

    let query = '';
    let endpoint = '';

    switch (role.role) {
        case Role.Manager:
            endpoint = 'manageractivelist';
            query = `?type=${type}`;
            break;
        case Role.SectionManager:
            endpoint = 'sectionmanageractivelist';
            query = `?type=${type}`;
            break;
        case Role.Employee:
            endpoint = 'employeeactivelist';
            break;
        default:
            break;
    }

    if (endpoint === '') {
        return Promise.resolve(null);
    }

    const promise = resgate.collection<SalaryReviewItem>(`v1.pay-management.review.${instanceTopic}.${currentOrganisationNodeId}.${endpoint}${query}`).then(res => {
        if (res?.success === false) return null;
        return res.data;
    });
    return promise;
}

/**
 * Get menu list
 *  @param currentRole current user role
 *  @param currentOrganisationId current organisation id
 *  @param type Salary review list type
 *  @param componentId Id of component calling the function
 */
export async function activeSalaryReviewListV2(resgate: ReturnType<typeof useResgate>, type: SalaryReviewListTypes): Promise<Iterable<SalaryReviewItem> | null> {
    const { instanceTopic, role, currentOrganisationNodeId, employee } = useLoginStore();
    if (instanceTopic == null) {
        return Promise.resolve(null);
    }

    if (role == null || currentOrganisationNodeId == null) {
        return Promise.resolve(null);
    }

    if (employee == null && role.role === Role.Employee) {
        return Promise.resolve(null);
    }

    let query = '';
    let endpoint = '';
    let rid = `v1.pay-management.review.${instanceTopic}.${currentOrganisationNodeId}`;

    switch (role.role) {
        case Role.Manager:
            endpoint = 'manageractivelist';
            query = `?type=${type}`;
            break;
        case Role.SectionManager:
            endpoint = 'sectionmanageractivelist';
            query = `?type=${type}`;
            break;
        case Role.Employee:
            if (employee?.id == null) { // this checks just exists to satisfy typescript, the employee null case is already handled above, so this should never be true
                return Promise.resolve(null);
            }
            endpoint = 'employeeactivelist';
            rid = `v2.pay-management.review.${instanceTopic}.${employee.id}`;
            break;
        default:
            break;
    }

    if (endpoint === '') {
        return Promise.resolve(null);
    }

    const promise = resgate.collection<SalaryReviewItem>(`${rid}.${endpoint}${query}`).then(res => {
        if (res?.success === false) return null;
        return res.data;
    });
    return promise;
}

/**
 * Get new filter table data
 * @param reviewId review id
 */
export async function getCurrencyConversions(salaryReviewId: string): Promise<CurrencyConversion[]> {
    const { instance } = useLoginStore();
    return payManagementService.post<CurrencyConversion[]>(null, `v1/${instance}/pay-management/review/${salaryReviewId}/currencyconversions`);
}

/**
 * Fetch new salaries for review
 * @param reviewId review id
 * @param negotiationRound negotiationRound
 */
export async function exportNewSalaryToVisma(salaryReviewId: string, excludeNonChanged: boolean): Promise<string> {
    const { instance } = useLoginStore();
    return payManagementService.post<string>({
        reviewId: salaryReviewId,
        excludeNonChanged
    }, `v1/${instance}/pay-management/exportvismap`);
}

/**
 * Fetch new salaries for review
 * @param reviewId review id
 */
export async function exportNewSalaryToECompanion(salaryReviewId: string): Promise<string> {
    const { instance } = useLoginStore();
    return payManagementService.post<string>({
        reviewId: salaryReviewId
    }, `v1/${instance}/pay-management/exportecompanion`);
}

export function getManagerIndividualBudgetKeyValues(resgate: ReturnType<typeof useResgate>, salaryReviewId: string):Promise<Iterable<ReportBudgetResponse> | null> | null {
    const { instanceTopic } = useLoginStore();
    if (instanceTopic == null) {
        return Promise.resolve(null);
    }
    return resgate.collection<ReportBudgetResponse>(`v1.pay-management.subdomain.${instanceTopic}.budget.${salaryReviewId}.managerkeyvalues`).then(res => {
        if (res?.success === false) return null;
        return res.data;
    });
}
